import Box from "@/common/components/Box";
import Select from "@/common/components/Select";
import ProductItem from "./ProductItem";
import { useFieldArray, useFormContext } from "react-hook-form";
import { ConsumptionValidationSchema } from "../schemas/create.schema";
import { useEffect } from "react";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { formatCurrency } from "@/common/utils/formatters";
import { Mapper } from "@/common/services/mapper";
import { ProductData } from "@/features/products/types/product";
import { numberToFixedHalfDown } from "@/common/utils/numberToFixedHalfDown";
import { useCardApplicableProducts } from "@/features/consumption/hooks/useCardApplicableProducts";
import useDebounce from "@/common/hooks/useDebounce";

const ProductsForm = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ConsumptionValidationSchema>()

  const { append: appendLineItem, remove: removeLineItem } = useFieldArray({
    control,
    name: "line_items_attributes",
  });

  const watchProducts = watch("line_items_attributes") || [];
  const cardId = watch("card_id");
  const establishmentId = watch("establishment_branch_id");

  const [searchProduct, setSearchProduct] = useDebounce('')

  const { data: products } = useCardApplicableProducts({
    filters: {
      search: searchProduct,
      cardId,
      establishmentId: establishmentId?.value,
    },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: "display_name", valueFieldName: "id" })
  });

  const handleRemoveProduct = (index: number) => {
    removeLineItem(index);
  };

  const handleAppendProduct = (option: { value: string, label: string; }) => {
    if ((option as { value: string; }).value) {
      appendLineItem({
        product_id: option?.value,
        quantity: "",
        unitary_amount: "",
        amount: "0",
        display_name: option?.label,
      });
    }
  };

  useEffect(() => {
    if (watchProducts) {
      const totalQuantity = watchProducts?.reduce((acc: number, product: any) => {
        return acc + (unFormatUnknownUnit(product?.quantity) || 0);
      }, 0) || 0;

      const totalAmount = watchProducts?.reduce((acc: number, product: any) => {
        return acc + Number(numberToFixedHalfDown(((unFormatUnknownUnit(product?.quantity) || 0) * (Number(unFormatCurrency(product?.unitary_amount)) || 0)), 2));
      }, 0) || 0;

      setValue("total_quantity", totalQuantity);
      setValue("total_amount", totalAmount);

      watchProducts?.forEach((product: any, index: number) => {
        const totalProductAmount = (unFormatUnknownUnit(product.quantity) || 0) * (Number(unFormatCurrency(product.unitary_amount)) || 0);

        setValue(`line_items_attributes.${index}.amount`, formatCurrency(Number(numberToFixedHalfDown(totalProductAmount, 2))));
      });
    }
  }, [JSON.stringify(watchProducts)]);

  return (
    <div className="flex flex-col gap-6 h-full">
      <p className="text-[20px] font-bold">
        Produtos transacionados
      </p>

      <Box className="flex flex-col gap-6 h-full">
        <div className="flex flex-col gap-2">
          <Select
            name="transaction_products"
            data-testid="transaction_products_selector"
            placeholder="Pesquisar produtos"
            onInputChange={(e) => setSearchProduct(e)}
            options={
              products?.filter(
                (product) => !watchProducts?.some(({ product_id }) => product_id === product.value),
              ) || []
            }
            isDisabled={!cardId || !establishmentId?.value}
            value=""
            onChange={(option) => handleAppendProduct(option as { value: string; label: string; })}
            noOptionsMessage={
              cardId && establishmentId
                ? () => 'Este estabelecimento não possui produtos autorizados para este consumo.'
                : undefined
            }
          />

          {errors?.line_items_attributes && <p className="text-[14px] text-danger-soft">{errors?.line_items_attributes?.message}</p>}
        </div>

        <div className="flex flex-col gap-4 overflow-y-auto">
          {watchProducts?.map((field, index) => (
            <ProductItem
              key={field.product_id + index}
              index={index}
              field={field}
              onDelete={() => handleRemoveProduct(index)}
            />
          ))}
        </div>
      </Box>
    </div>
  );
};

export default ProductsForm;
